import ServiceContent, { IServiceContent } from "@/model/Content/ServiceContent"
import EquipmentServiceContent from "@/model/Content/EquipmentServiceContent"
import SkiSchoolServiceContent from "@/model/Content/SkiSchoolServiceContent"
import FoodServiceContent from "@/model/Content/FoodServiceContent"
import ParkingServiceContent from "@/model/Content/ParkingServiceContent"
import SkipassServiceContent from "@/model/Content/SkipassServiceContent"
import TransportServiceContent from "@/model/Content/TransportServiceContent"

import { IServiceFamily } from "@/model/ServiceFamily/ServiceFamily"
import AccomodationServiceFamily from "@/model/ServiceFamily/AccomodationServiceFamily"
import GlobalServiceFamily from "@/model/ServiceFamily/GlobalServiceFamily"
import ParticipantServiceFamily from "@/model/ServiceFamily/ParticipantServiceFamily"
import EquipmentServiceFamily from "@/model/ServiceFamily/EquipmentServiceFamily"
import SkiSchoolServiceFamily from "@/model/ServiceFamily/SkiSchoolServiceFamily"
import FoodServiceFamily from "@/model/ServiceFamily/FoodServiceFamily"
import ParkingServiceFamily from "@/model/ServiceFamily/ParkingServiceFamily"
import SkipassServiceFamily from "@/model/ServiceFamily/SkipassServiceFamily"
import TransportServiceFamily from "@/model/ServiceFamily/TransportServiceFamily"

import { ServiceContent as DTOServiceContent } from "@requests"

import giraffe from "@/assets/giraffe.jpg"
import { criteriaToName, criteriaToDescription, criteriaToImage, criteriaToImage_90deg, familyIdToImage } from "@/config/defaultContentsMaps"

type Image = any


export interface IServiceContentFactory { }

class ServiceContentFactory implements IServiceContentFactory {

    private static getImageByCriterias(criterias: string[], is90deg: boolean): Image | undefined {
        const criteriasSet = new Set(criterias)
        const imageMap = is90deg ? criteriaToImage_90deg : criteriaToImage
        const entry = Array.from(imageMap.entries()).find(([criteriasArr, image]) => {
            let isHaveAllCriterias = true
            criteriasArr.forEach((criteria) => {
                if (!criteriasSet.has(criteria)) isHaveAllCriterias = false
            })
            if (isHaveAllCriterias) return true
            return false
        })
        if (!entry) return undefined
        const image = entry[1]
        return image
    }

    private static getNameByCriterias(criterias: string[]): string | undefined {
        const criteriasSet = new Set(criterias)
        const entry = Array.from(criteriaToName.entries()).find(([criteriasArr, contentName]) => {
            let isHaveAllCriterias = true
            criteriasArr.forEach((criteria) => {
                if (!criteriasSet.has(criteria)) isHaveAllCriterias = false
            })
            if (isHaveAllCriterias) return true
            return false
        })
        if (!entry) return undefined
        const contentName: string = entry[1]
        return window.content[contentName]
    }

    private static getDescriptionByCriterias(criterias: string[]): string | undefined {
        const criteriasSet = new Set(criterias)
        const entry = Array.from(criteriaToDescription.entries()).find(([criteriasArr, contentName]) => {
            let isHaveAllCriterias = true
            criteriasArr.forEach((criteria) => {
                if (!criteriasSet.has(criteria)) isHaveAllCriterias = false
            })
            if (isHaveAllCriterias) return true
            return false
        })
        if (!entry) return undefined
        const contentName: string = entry[1]
        return window.content[contentName]
    }

    private static getImageByServiceFamily(serviceFamily: IServiceFamily): Image | undefined {
        const familyId = serviceFamily.getId()
        return familyIdToImage.get(familyId)
    }

    private static getDefaultImage(serviceFamily: IServiceFamily, criterias: string[], is90deg = false) {
        // try to return the default image according criterias
        const imageByCrit = ServiceContentFactory.getImageByCriterias(criterias, is90deg)
        if (imageByCrit) return imageByCrit

        // try to return a default image according family id
        const imageByFamily = ServiceContentFactory.getImageByServiceFamily(serviceFamily)
        if (imageByFamily) return imageByFamily

        // return a giraffe, because it good long boi
        return giraffe
    }

    private static getContentClass(serviceFamily: IServiceFamily): Constructor<IServiceContent> {
        if (serviceFamily instanceof EquipmentServiceFamily) { return EquipmentServiceContent }
        if (serviceFamily instanceof SkiSchoolServiceFamily) { return SkiSchoolServiceContent }
        if (serviceFamily instanceof FoodServiceFamily) { return FoodServiceContent }
        if (serviceFamily instanceof ParkingServiceFamily) { return ParkingServiceContent }
        if (serviceFamily instanceof SkipassServiceFamily) { return SkipassServiceContent }
        if (serviceFamily instanceof TransportServiceFamily) { return TransportServiceContent }
        if (serviceFamily instanceof AccomodationServiceFamily) { return ServiceContent }
        if (serviceFamily instanceof GlobalServiceFamily) { return ServiceContent }
        if (serviceFamily instanceof ParticipantServiceFamily) { return ServiceContent }
        return ServiceContent
    }

    public static createServiceContent(contentObj: DTOServiceContent | undefined, serviceFamily: IServiceFamily): IServiceContent {
        const ContentConstructor = ServiceContentFactory.getContentClass(serviceFamily)
        const content = new ContentConstructor()

        // set generic fields
        if (contentObj?.code) { content.setCode(contentObj.code) }
        if (contentObj?.nameCommercial) { content.setNameCommercial(contentObj.nameCommercial) }
        if (contentObj?.fournisseurId) { content.setFournisseurId(contentObj.fournisseurId) }
        if (contentObj?.fournisseurName) { content.setFournisseurName(contentObj.fournisseurName) }
        if (contentObj?.family) { content.setFamily(contentObj.family) }
        if (contentObj?.description) { content.setDescription(ServiceContentFactory.getDescriptionByCriterias(contentObj.criterias || []) || contentObj.description) }
        if (contentObj?.name) { content.setName(ServiceContentFactory.getNameByCriterias(contentObj.criterias || []) || contentObj.name) }
        if (contentObj?.name) { content.setName(contentObj.name) }
        if (contentObj?.criterias) { content.setCriterias(contentObj.criterias) }
        if (contentObj?.additionalInformation) { content.setAdditionalInformation(contentObj.additionalInformation) }
        if (contentObj?.link) { content.setLink(contentObj.link) }
        if (contentObj?.skiSchool) { content.setSkiSchool(contentObj.skiSchool) }
        if (content instanceof SkipassServiceContent) {
            if (contentObj?.nbSlopesBlack) content.nbSlopesBlack = contentObj.nbSlopesBlack
            if (contentObj?.nbSlopesBlue) content.nbSlopesBlue = contentObj.nbSlopesBlue
            if (contentObj?.nbSlopesGreen) content.nbSlopesGreen = contentObj.nbSlopesGreen
            if (contentObj?.nbSlopesRed) content.nbSlopesRed = contentObj.nbSlopesRed
            if (contentObj?.slopeLength) content.slopeLength = contentObj.slopeLength
        }

        // set image and image rotated 90 degrees (for equipment boxes on mobile, for example)
        const criterias = content.getCriterias()
        const is90deg_TRUE = true // just for readability
        const is90deg_FALSE = false // just for readability
        const image = contentObj?.image ? `https://asset.travelfactory.fr${contentObj.image}` : ServiceContentFactory.getDefaultImage(serviceFamily, criterias, is90deg_FALSE)
        content.setImage(image)
        const image_90deg = contentObj?.image_90deg ? `https://asset.travelfactory.fr${contentObj.image_90deg}` : ServiceContentFactory.getDefaultImage(serviceFamily, criterias, is90deg_TRUE)
        content.setImage_90deg(image_90deg)

        // set custom fields for special content types
        if (content instanceof TransportServiceContent) {
            if (contentObj?.transportation) content.setTransportation(contentObj.transportation)
            if (contentObj?.transportationBack) content.setTransportationBack(contentObj.transportationBack)
        }
        return content
    }
}

export default ServiceContentFactory